/**
 * Shared module definitions.
 * Single source of truth for nav access, keyed by module ID.
 * Both AppHeader (nav strip) and useDashboard import from here.
 */

// Ordered list of navigable modules (no coming-soon entries).
//
// `label` is the ENGLISH FALLBACK, not the rendered string. AppHeader draws `t(navKey || id) ||
// label` against shared/i18n/dict/*/modules.js, so a module added here without a dictionary entry
// still gets a readable English button instead of a blank one — see that dictionary's header.
//
// `navKey` exists only where the strip's wording is SHORTER than the portal tile's for the same
// module ("HR" here, "HR Management" on the tile). Without it the two surfaces would need separate
// dictionaries and would drift the way their English already has.
export const NAV_MODULES = [
  { id: 'identity',    label: 'My Profile', icon: '👤', path: '/profile' },
  { id: 'timekeeping', label: 'Timekeeping', icon: '⏰', path: '/timekeeping' },
  { id: 'projects',    label: 'Projects',    icon: '📊', path: '/projects' },
  { id: 'crm',         label: 'CRM',         icon: '🤝', path: '/crm' },
  { id: 'hr',          label: 'HR',          icon: '👥', path: '/hr',       navKey: 'hrShort' },
  { id: 'bidworkup',   label: 'Bid Workup',  icon: '🏗️', path: '/bidworkup' },
  { id: 'finance',     label: 'Finance',     icon: '💰', path: '/finance',  navKey: 'financeShort' },
  { id: 'documents',   label: 'Documents',   icon: '📄', path: '/documents', navKey: 'documentsShort' },
  { id: 'fleet',       label: 'Fleet',       icon: '🚛', path: '/fleet',    navKey: 'fleetShort' },
  { id: 'inventory',   label: 'Inventory',   icon: '📦', path: '/inventory' },
  { id: 'officeboard', label: 'Office Board', icon: '📋', path: '/office-board' },
  { id: 'pdfreader',   label: 'PDF Reader',   icon: '📐', path: '/pdf-reader' },
  { id: 'forms',       label: 'Forms',        icon: '📝', path: '/forms' },
  // NO "Capital Projects" TAB (owner, 30 Sep 2026: Project Financials is the one place for capital work).
  // Its board, its estimates and a capital project's award baseline are all in Project Financials, and an
  // old /capital address forwards there (modules/capital/CapitalModule.jsx). The `capital` SWITCH stays in
  // HELD_MODULES below: the capital estimate's server routes still answer only while it is on.
  // Capex — the running project's financials (WIP, cost, change orders, billing, cash flow,
  // risk, closeout, bids, and capital estimates). Reads from Projects.
  { id: 'capex',       label: 'Project Financials', icon: '📉', path: '/capex' },
  // Invoicing — the billing documents themselves: pay applications, T&M invoices, receivables,
  // cash, lien waivers. Capex's twin (same permission gate); see shared/api/config.js.
  { id: 'invoicing',   label: 'Invoicing',    icon: '🧾', path: '/invoicing' },
  // The AI Office Worker (design change H2) — a separately versioned product (products/office-worker)
  // that runs INSIDE Tynvara: its tab opens /office-worker (modules/office_worker/OfficeWorkerRoute.jsx),
  // which is the Office Worker's own inbox, signed in by this app's token. Held like the three above, on
  // OFFICE_WORKER_ENABLED plus the company key.
  { id: 'office_worker', label: 'Office Worker', icon: '🤖', path: '/office-worker' },
]

// Modules gated by a per-tenant feature flag (features_enabled) would appear ONLY when the tenant has the
// feature on, independent of the role map. Currently EMPTY: forms is now a standard always-on module (in
// the role nav defaults below + un-gated on the backend). Kept as the mechanism for any future
// entitlement-gated module.
export const FEATURE_GATED = {}

// THE HELD-MODULES LIST (update 2, plan §1.3; owner decisions 6a and 17a). These modules are INSTALLED —
// their programs run, their tables exist, login still hands their ids to every role that was seeded
// them — and they are HELD BACK until the server says otherwise. Admins included: nothing about the
// user can release one.
//
// The list is compiled into every build. What releases a module is NOT the build: it is the server's
// switch (CAPITAL_MODULE_ENABLED / CAPEX_MODULE_ENABLED / INVOICING_MODULE_ENABLED / OFFICE_WORKER_ENABLED
// on the backend task),
// read at runtime from GET /api/identity/modules/switches (contexts/ModuleSwitchContext.jsx). One image
// of each kind serves staging and production; staging's task turns the switches on and production's
// sets nothing, so the SAME bundle hides them on production and shows them on staging, and the screen
// obeys exactly the switch the server refuses by (shared/module_switches.py). Since switch-on plan S1
// that answer is also PER COMPANY: a module is released only where the environment switch is on AND a
// platform admin has turned it on for the signed-in person's company, so one company can have Capex in
// production while every other company's admins still see nothing.
//
// NOT FEATURE_GATED above, on purpose: that is a per-TENANT entitlement from `features_enabled`, which a
// company admin can hold, and the server does not read it for these modules. A tile released by a
// tenant flag would open onto a server that still refuses it.
//
// Held means hidden from the nav strip (getAccessibleNavModules), the portal tiles (Portal.jsx), the
// address (ModuleSwitchRoute in App.jsx) and tours (tourSelectors.js). It FAILS CLOSED: while the answer
// is loading, if it never arrives, or for any value but `true`, the module stays held.
//
// 'office_worker' (design change H2) is held from its first day: the AI Office Worker reaches Tynvara
// only through the agent host, and every agent-host function asks the same two keys, so the tab, the
// tile and the address must obey them too or they would open onto a refusal.
export const HELD_MODULES = ['capital', 'capex', 'invoicing', 'office_worker']

// THE STANDARD MODULES (owner, 29 Sep 2026: "the on/off for each module for each tenant"). The twelve
// every company has today. A platform admin can switch one OFF for one company on the Super Admin page
// (shared/module_switches.py COMPANY_SWITCHES), and then its tile, its tab, its address and its
// service all go for that company, exactly as a held module's do.
//
// THE OTHER WAY ROUND FROM HELD_MODULES, and kept a separate list so nobody reads one for the other:
// a standard module is ON unless the server's answer says `false` for it. While the answer is on its
// way, if it never arrives, or for anything but a literal `false`, the module shows as it always has -
// nothing may disappear by itself. My Profile (identity) is in neither list: it is never switched.
export const STANDARD_MODULES = [
  'timekeeping', 'projects', 'crm', 'hr', 'bidworkup', 'finance',
  'documents', 'fleet', 'inventory', 'officeboard', 'pdfreader', 'forms',
]

// Module ids that are not nav modules of their own but live inside one: they follow that module's
// switch. `bid_request` (the foreman's Bid Request tile and dashboard figures) is a Bid Workup record,
// served by the bid workup service, so it goes when Bid Workup is switched off. `procurement` (the
// Procurement tile) is purchasing, which lives inside Project Financials - its purchase orders are on Capex
// jobs (docs/PURCHASING.md) - so it goes when Project Financials is switched off.
export const SWITCHED_WITH = { bid_request: 'bidworkup', procurement: 'capex' }

/**
 * Whether a module is served to this company, by ITS rule: a held module only when its switch is
 * `true`, a standard module unless its switch is `false`, anything else (My Profile) always.
 */
export const isModuleReleased = (id, switches) => {
  const moduleId = SWITCHED_WITH[id] || id
  if (HELD_MODULES.includes(moduleId)) return switches?.[moduleId] === true
  if (STANDARD_MODULES.includes(moduleId)) return switches?.[moduleId] !== false
  return true
}

// Default nav module IDs per system role.
// Custom roles fall back to their accessible_modules list (explicit override).
//
// 'office_worker' sits on the same roles as 'invoicing' — the company's administrators and its office
// (money and project) roles — and, like every held module, shows only once BOTH switch keys release it.
const ROLE_NAV_DEFAULTS = {
  // System
  master_admin:             ['identity','timekeeping','projects','hr','crm','bidworkup','finance','documents','fleet','inventory','officeboard','pdfreader','forms','capex','invoicing','office_worker'],
  auditor:                  ['identity','projects','finance','documents','inventory','officeboard','forms'],
  // Administrative
  admin:                    ['identity','timekeeping','projects','hr','crm','bidworkup','finance','documents','fleet','inventory','officeboard','pdfreader','forms','capex','invoicing','office_worker'],
  operations_administrator: ['identity','timekeeping','projects','crm','bidworkup','documents','fleet','inventory','officeboard','pdfreader','forms','capex','invoicing','office_worker'],
  hr_administrator:         ['identity','timekeeping','hr','documents','officeboard','forms'],
  finance_administrator:    ['identity','timekeeping','hr','finance','documents','inventory','officeboard','forms','capex','invoicing','office_worker'],
  // Management
  principal:                ['identity','timekeeping','projects','hr','crm','bidworkup','finance','documents','fleet','inventory','officeboard','pdfreader','forms','capex','invoicing','office_worker'],
  executive:                ['identity','timekeeping','projects','hr','crm','bidworkup','finance','documents','fleet','inventory','officeboard','pdfreader','forms','capex','invoicing','office_worker'],
  operations_manager:       ['identity','timekeeping','projects','crm','bidworkup','documents','fleet','inventory','officeboard','pdfreader','forms','capex','invoicing','office_worker'],
  manager:                  ['identity','timekeeping','projects','crm','bidworkup','documents','fleet','inventory','officeboard','forms','capex','invoicing','office_worker'],
  superintendent:           ['identity','timekeeping','projects','documents','fleet','inventory','officeboard','forms'],
  foreman:                  ['identity','timekeeping','projects','bidworkup','fleet','inventory','officeboard','forms'],
  // Staff
  // 'capex': capital estimates live in Project Financials since PLAN-28 P2 (estimates half only).
  estimator:                ['identity','crm','bidworkup','documents','pdfreader','officeboard','forms','capex'],
  project_engineer:         ['identity','timekeeping','projects','documents','officeboard','forms'],
  timekeeper:               ['identity','timekeeping','projects','officeboard','forms'],
  accounting_staff:         ['identity','timekeeping','finance','documents','inventory','officeboard','forms','capex','invoicing','office_worker'],
  payroll_staff:            ['identity','timekeeping','hr','documents','officeboard','forms'],
  procurement_staff:        ['identity','projects','crm','documents','inventory','officeboard','forms'],
  safety_officer:           ['identity','projects','documents','fleet','officeboard','forms'],
  document_controller:      ['identity','projects','documents','officeboard','forms'],
  administrative_staff:     ['identity','timekeeping','documents','officeboard','forms'],
  employee:                 ['identity','timekeeping','officeboard','forms'],
  // Field
  field_worker:             ['identity','timekeeping','officeboard','forms'],
  equipment_manager:        ['identity','projects','documents','fleet','inventory','officeboard','forms'],
  equipment_operator:       ['identity','timekeeping','fleet','inventory','officeboard','forms'],
  inspector:                ['identity','projects','documents','fleet','officeboard','forms'],
  // External
  client:                   ['identity','bidworkup'],
  vendor:                   ['identity'],
  subcontractor:            ['identity','timekeeping'],
}

/**
 * The modules the server's list (`user.accessible_modules`) GRANTS for display: the ONE place the nav
 * strip (getAccessibleNavModules below) and the portal tiles (routes/Portal.jsx) read that list. Null
 * when the server sent no list, so each caller falls back to its role map as before.
 *
 * A 'capital' GRANT ALSO OPENS PROJECT FINANCIALS (owner, 30 Sep 2026: the Capital Projects tab and tile
 * are retired; Project Financials is the one place for capital work). A role given Capital Projects - by
 * the seed or in the Roles editor - but not Project Financials would otherwise have lost its only door to
 * capital work. So 'capital' in the list counts as 'capex' too. DISPLAY ONLY: no permission, seed or
 * server rule changes; Project Financials still shows only where its own switch is on (isModuleReleased),
 * and what can be opened inside is still the servers' decision.
 */
export const grantedModuleIds = (user) => {
  const list = user?.accessible_modules
  if (!list?.length) return null
  return list.includes('capital') && !list.includes('capex') ? [...list, 'capex'] : list
}

/**
 * Returns the ordered list of NAV_MODULES the user can access.
 * Used by AppHeader to render the nav strip.
 *
 * ⚠ THIS IS A COURTESY, NOT A CONTROL. IT HIDES; IT DOES NOT DENY.
 *
 * It is right that this exists — showing somebody a tab they will only be refused from is a bad
 * screen — but nothing here is a security boundary, and it has been mistaken for one. As of
 * 2026-09-08 `accessible_modules` is read by NO backend authorization check anywhere. It is not in
 * the JWT either (identity routers/auth.py builds `token_data` without it), so no server dependency
 * could consult it without an extra database lookup per request. Its only producers are the login
 * and refresh response bodies (routers/auth.py:386-407 and :708-726, filled from the
 * `role_module_defaults` table) and the admin write route (routers/user.py:454). Its only consumer
 * is this function.
 *
 * WHAT THAT MEANS IN PRACTICE: a user whose role omits a module sees no tab for it and is still
 * served by that module's API if they call it directly. Whether that matters is per-module, and it
 * is the MODULE'S OWN ROUTES that must say no — `require_permission` on the route, the way the
 * gated modules already do it. Do not add a second authorization path through this list: a parallel
 * mechanism is a second thing to keep in step, and the two diverge on the first module somebody adds.
 *
 * TWO THINGS IN HERE THAT LOOK LIKE FACTS AND ARE NOT:
 *   - `ROLE_NAV_DEFAULTS` below is DEAD for every seeded role. The branch that reads it fires only
 *     when `accessible_modules` is empty, and login always fills it for a role that has module rows.
 *     It also disagrees with the live seed (identity database/session.py, `_SYSTEM_ROLES`) — it omits
 *     documents from foreman, which the seed grants, and it names ~29 roles where the seed creates 9.
 *     It is a fallback, not the role/module table. Read the seed.
 *   - the module id is `pdfreader`, one word. `pdf_reader` is only the backend directory name, and a
 *     grep or migration written against the underscore form silently matches nothing.
 */
export const getAccessibleNavModules = (user, features = {}, switches = {}) => {
  if (!user) return []
  const role = user.role?.toLowerCase() || ''

  let ids
  const granted = grantedModuleIds(user)
  if (granted) {
    // Explicit per-user override — always include identity
    ids = granted.includes('identity') ? granted : ['identity', ...granted]
  } else {
    ids = ROLE_NAV_DEFAULTS[role] ?? ['identity']
  }

  const idSet = new Set(ids)
  return NAV_MODULES.filter(m => {
    // A held module shows only once the server's switch releases it — before the role map, before the
    // per-user list, before anything an admin holds. `switches` defaults to {} so a caller that forgets
    // to pass it hides the module rather than showing one the server refuses.
    if (!isModuleReleased(m.id, switches)) return false
    // A feature-gated module shows IFF the tenant has the feature on (regardless of the role map);
    // every other module follows the role/override list as before.
    if (FEATURE_GATED[m.id]) return Boolean(features?.[FEATURE_GATED[m.id]])
    return idSet.has(m.id)
  })
}
